import { observer } from "mobx-react";
import { useMemo } from "react";
import { ScopePicker } from "@/components/shared/scope-picker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n";
import type { Scope, ScopeCategory } from "@/services/oauth-idp.service";
import type { TTeammateScope } from "@/types/teammates";

interface TeammatePermissionsListProps {
  /** Every scope the teammate may hold here, with its description. */
  scopes: TTeammateScope[];
  /** Scopes it cannot work without; marked "Always granted". */
  requiredScopes?: string[];
}

/** The teammate's scopes as the shared picker's catalog. The API types a
 *  scope's category as a plain string; the picker groups any category it has
 *  no heading for under "Other" (and warns), so an unknown one still renders. */
function asCatalog(scopes: TTeammateScope[]): Scope[] {
  return scopes.map((scope) => ({ ...scope, category: scope.category as ScopeCategory }));
}

/**
 * The full permission grid of one digital teammate (NG-2622, design F3): the
 * shared `ScopePicker`, read-only and grouped by category, every scope shown
 * as held. A scope the teammate cannot work without is locked, which the
 * picker labels "Always granted".
 */
export const TeammatePermissionsList = observer(function TeammatePermissionsList({
  scopes,
  requiredScopes,
}: TeammatePermissionsListProps) {
  const { t } = useTranslation();
  const catalog = useMemo(() => asCatalog(scopes), [scopes]);
  const held = useMemo(() => scopes.map((scope) => scope.name), [scopes]);
  return (
    <div className="space-y-3">
      {requiredScopes && requiredScopes.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("digital_teammates.permissions.required_note")}
        </p>
      ) : null}
      <ScopePicker
        catalog={catalog}
        value={held}
        onChange={() => {}}
        locked={requiredScopes}
        readOnly
      />
    </div>
  );
});

interface TeammatePermissionsDialogProps extends TeammatePermissionsListProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The teammate's display name, e.g. "Reporting Teammate". */
  name: string;
}

/** "View full permissions": the teammate's permission grid in a dialog. One
 *  component for the admin's and the member's screen (design F3). */
export const TeammatePermissionsDialog = observer(function TeammatePermissionsDialog({
  open,
  onOpenChange,
  name,
  scopes,
  requiredScopes,
}: TeammatePermissionsDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("digital_teammates.permissions.title", { name })}</DialogTitle>
          <DialogDescription>
            {t("digital_teammates.permissions.description", { name })}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-x-hidden overflow-y-auto pr-3">
          <TeammatePermissionsList scopes={scopes} requiredScopes={requiredScopes} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
